import { Trans } from '@lingui/react'
import {
  type NowMerchantExtended,
  type NowSlot,
} from '@redteclab/api/clients/bully'
import classNames from 'classnames'
import {
  type Dispatch,
  type FormEvent,
  type ReactElement,
  type SetStateAction,
  useEffect,
} from 'react'

import floatingLabel from 'views/assets/scripts/floatingLabel'
import Button from 'views/components/atoms/Button'
import {
  currencyToSymbol,
  formatPriceString,
} from 'views/components/atoms/Price/helpers'
import InputTextWithFloatingLabel from 'views/components/molecules/InputTextWithFloatingLabel'
import NowDeliveryBox from 'views/components/organisms/NowShippingModal/NowShippingModalContent/NowDeliveryBox'
import TransWithHtml from 'views/components/utils/TransWithHtml'
import {
  MINIMUM_VALUE_NOT_REACHED,
  NO_AVAILABLE_SLOT,
  NOT_IN_DELIVERY_REGION,
  PRODUCTS_NOT_AVAILABLE,
  RX_NOT_AVAILABLE,
} from 'views/consts/notEligibleReasons'
import 'views/components/organisms/NowShippingModal/NowShippingModal.scss'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'

import Spinner from './Spinner'

export interface INowSlotExtended extends NowSlot {
  shippingCostsAmountSpecial: number
  shippingCostsAmountDefault: number
}

export interface INowDeliveryBoxData {
  slot: INowSlotExtended
  merchant: NowMerchantExtended
  centsDifferenceMinimumOrderValue: number | undefined
}

interface INowZipForm {
  nowEligible: boolean
  notEligibleReasonCode?: number
  zipCode: string
  setZipCode: Dispatch<SetStateAction<string>>
  handleSubmit: (zipCode: string) => void
  setErrorMessage: Dispatch<SetStateAction<string>>
  isLoading?: boolean
  nowDeliveryBoxData: INowDeliveryBoxData | null
  errorMessage?: string
}

const NowZipForm: (props: INowZipForm) => ReactElement = ({
  nowEligible,
  notEligibleReasonCode,
  zipCode,
  setZipCode,
  handleSubmit,
  isLoading,
  nowDeliveryBoxData,
  errorMessage,
}) => {
  const onSubmitHandler = (event: FormEvent) => {
    event.preventDefault()

    handleSubmit(zipCode)
  }

  useEffect(() => {
    floatingLabel()
  }, [])

  const {
    publicRuntimeConfig: {
      publicConfig: { locale, currencySymbolRight },
    },
  } = useShopAndRuntimeConfig()

  const differenceMinimumOrderValue =
    nowDeliveryBoxData?.centsDifferenceMinimumOrderValue
      ? nowDeliveryBoxData?.centsDifferenceMinimumOrderValue / 100
      : 0

  const currencySymbol = currencyToSymbol('EUR')
  const diffPrice = formatPriceString({
    amount: differenceMinimumOrderValue,
    locale,
    currencySymbol,
    symbolRight: currencySymbolRight,
  })

  const notEligibleMessage = () => {
    switch (notEligibleReasonCode) {
      case PRODUCTS_NOT_AVAILABLE:
        return <Trans id="general.label.now.productNotAvailable" />
      case NO_AVAILABLE_SLOT:
        return (
          <Trans id="general.label.now.collapsible.error.no_available_slot" />
        )
      case RX_NOT_AVAILABLE:
        return <Trans id="general.label.now.productNotAvailable" />
      case MINIMUM_VALUE_NOT_REACHED:
        return (
          <TransWithHtml
            id="general.label.now.collapsible.error.minimum_value_not_reached"
            values={{ diffPrice }}
            element={'span'}
            className={'o-NowCollapsible__error40'}
          />
        )
      case NOT_IN_DELIVERY_REGION:
        return <Trans id="general.label.now.zipNotAvailable" />
    }
  }

  return (
    <div>
      <p>
        <Trans id="general.label.now.description" />
      </p>
      <div
        className={classNames('o-NowShippingWrapper', 'u-margin-large--top', {
          'o-nowEligible': nowEligible,
          'o-nowNotEligible': !!notEligibleReasonCode || !!errorMessage,
        })}
      >
        <h3>
          <Trans id="general.label.now.checkAvailability" />
        </h3>
        <form
          onSubmit={onSubmitHandler}
          className="l-flex l-flex__column l-flex__row@medium"
        >
          <div className={'o-NowInput'}>
            <InputTextWithFloatingLabel
              value={zipCode}
              onChange={e => {
                setZipCode(e.target.value)
              }}
              name="pzn-input"
              inputType="text"
            >
              <Trans id="general.label.now.zipcode" />
            </InputTextWithFloatingLabel>
          </div>
          <Button
            type="submit"
            name="btnConfirm"
            className="o-NowButtonMargin"
            style="buttonPrimary"
          >
            {isLoading ? (
              <Spinner />
            ) : (
              <Trans id="general.label.now.checkNow" />
            )}
          </Button>
        </form>
        {errorMessage && (
          <ul className="o-nowUlInformation u-margin-large--top a-list-reset">
            <li className="o-nowErrorColor">
              <span className="o-errorMessage">{errorMessage}</span>
            </li>
          </ul>
        )}
        {!!notEligibleReasonCode && !errorMessage && (
          <ul className="o-nowUlInformation u-margin-large--top a-list-reset">
            <li className="o-nowLiColor">{notEligibleMessage()}</li>
          </ul>
        )}
        {nowEligible && !errorMessage && (
          <ul className="o-nowUlInformation u-margin-large--top a-list-reset o-nowEligibleTextColor">
            <li className="o-nowEligibleLiColor o-nowEligibleTextColor">
              <Trans id="general.label.now.zipAvailable" />
            </li>
          </ul>
        )}
      </div>
      {nowEligible && !errorMessage && (
        <NowDeliveryBox
          deliveryDay={nowDeliveryBoxData?.slot?.deliveryDay}
          minimumOrder={nowDeliveryBoxData?.merchant?.minimumOrderValue}
          deliveryCost={nowDeliveryBoxData?.slot?.shippingCostsAmountDefault}
          deliveryRedpoints={
            nowDeliveryBoxData?.slot?.shippingCostsAmountSpecial
          }
          secondsLeft={nowDeliveryBoxData?.slot?.secondsLeft}
          carrierName={nowDeliveryBoxData?.merchant?.merchantName}
          deliveryTimeInfo={nowDeliveryBoxData?.slot?.deliveryTimeInfo}
        />
      )}
    </div>
  )
}

export default withShopAndRuntimeConfig(NowZipForm)
